import React from "react";
import {
    Accordion,
    Alert,
    Checkbox,
    Dropdown,
    Flex,
    InfoIcon,
    Loader,
    Text,
    Tooltip,
} from "@fluentui/react-northstar";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import Badge from "../../../features/addEmployee/components/badges";
import { _api_get_model } from "../../../features/addEmployee/api";
import {
    _checkedChildrensTemplate,
    _checkedHeaderListTemplate,
} from "./functions";
import { Controller, useFormContext } from "react-hook-form";
import { DIALOG_ADD_TASK_RULES } from "../../../features/addEmployee/constants/validators";
import { DropdownFixed, ErrorMessage, Label } from "../../../common/styles";
import { getTaskResponsable, getTaskResponsables } from "../../../api/tasks";
import {
    BASE_URL_ILLUSTRATIONS,
    I18N_NS_TRANSLATION,
} from "../../../constants";
import { useBenefeciaryStore } from "./store/index.store";
import { DropdownStyle } from "./DialogAddManualTaskTemplate";

const _formatJsonTemplateToFuiPanels = (templates = [], setTemplates) => {
    const initJson = templates?.map((el) => {
        return {
            key: "key_" + el.id,
            title: (
                <Flex
                    key={"title_" + el.id}
                    vAlign="center"
                    styles={{
                        justifyContent: "space-between",
                        padding: "10px 15px 10px 0px",
                    }}
                >
                    <Flex
                        gap="gap.small"
                        vAlign="center"
                        styles={{ marginLeft: "5px" }}
                    >
                        <Checkbox
                            label={
                                <Text styles={{ fontWeight: "bold" }}>
                                    {el.name}
                                </Text>
                            }
                            onClick={_checkedHeaderListTemplate}
                            styles={{
                                height: "fit-content",
                                padding: "0px!important",
                            }}
                            className="Accordion__checkbox--header"
                            data={el}
                            checked={el?.checked}
                            templates={templates}
                            setTemplates={setTemplates}
                        />
                        <Tooltip
                            content={el.description}
                            trigger={<InfoIcon outline />}
                        />
                    </Flex>
                    <Badge content={el.type} />
                </Flex>
            ),
            content: (
                <Flex column key={"content_" + el.id}>
                    {el?.tasks?.map((child) => {
                        return (
                            <Checkbox
                                key={child.id}
                                label={<Text>{child.name}</Text>}
                                onClick={_checkedChildrensTemplate}
                                styles={{
                                    borderBottom:
                                        "1px solid rgba(219, 219, 219, 0.5)",
                                    paddingBlock: "14px",
                                }}
                                className="Accordion__checkbox--children"
                                data-parent={el}
                                data-children={child}
                                checked={child?.checked}
                                templates={templates}
                                setTemplates={setTemplates}
                                setTemp
                            />
                        );
                    })}
                </Flex>
            ),
        };
    });

    return initJson;
};

export const ContentImportTemplatesWithCollaborator = ({
    templates,
    setTemplates,
    typeFromPath,
}) => {
    const { t } = useTranslation(I18N_NS_TRANSLATION);
    const setBenefeciary = useBenefeciaryStore((state) => state.setBenefeciary);
    const {
        formState: { errors },
        control,
        setValue,
    } = useFormContext(); // retrieve all hook methods

    const { isLoading, isFetching } = useQuery(
        "getTemplatesCollaborator",
        () => {
            return _api_get_model();
        },
        {
            enabled: templates?.length === 0,
            onSuccess: (data) => {
                setTemplates(data);
                // f_set_templates(data);
                // f_set_templates_last_updated(data);
            },
        }
    );
    // get all collaborateur users.
    let { data: dataCollaborator = [], isLoading: isLoadingCollaborator } =
        useQuery(["getCollaborator"], () => getTaskResponsables());

    if (isLoading || isFetching || isLoadingCollaborator)
        return <p>loading ...</p>;

    const formatedTemplatesJson = _formatJsonTemplateToFuiPanels(
        templates,
        setTemplates
    );

    dataCollaborator = dataCollaborator.map((el) => {
        return {
            id: el?.id,
            key: el?.id,
            header: el.name,
            image: el.avatar || `${BASE_URL_ILLUSTRATIONS}/Avatar-defaut.svg`,
            content: el.job_title,
            username: el?.username,
        };
    });

    return templates?.length === 0 ? (
        <Flex
            styles={{ fontWeight: "600", padding: "32px 16px" }}
            fill
            hAlign="center"
            vAlign="center"
        >
            <Text
                color="brand"
                content={
                    typeFromPath
                        ? `Aucun modèle de tâche ${typeFromPath} trouvé !`
                        : "Aucun modèle de tâche trouvé !"
                }
            />
        </Flex>
    ) : (
        <Flex column gap="gap.medium" styles={{ marginTop: "10px" }}>
            <Flex column gap="gap.medium">
                <Text
                    content={t(
                        "pages.add_employee.step_tasks.dialog.description"
                    )}
                />
                <Alert
                    icon={<InfoIcon />}
                    content={
                        <Text
                            styles={{ fontWeight: "600" }}
                            content={t(
                                "pages.add_employee.step_tasks.dialog.alert"
                            )}
                        />
                    }
                />
            </Flex>
            <Flex column>
                {isLoading || isFetching ? (
                    <Loader label="Chargement des données !" />
                ) : (
                    <AccordionStyle
                        panels={formatedTemplatesJson}
                        key={"accordion"}
                    />
                )}
            </Flex>
            <Flex fill vAlign="center" styles={{ alignSelf: "center" }} column>
                <Label>Bénéficiaire*</Label>
                <Controller
                    control={control}
                    rules={DIALOG_ADD_TASK_RULES.assigned_to}
                    render={({
                        field: { onChange, onBlur, value, name, ref },
                        fieldState: { invalid, isTouched, isDirty, error },
                        formState,
                    }) => (
                        <DropdownStyle
                            search
                            positionFixed
                            width={"536px"}
                            items={dataCollaborator}
                            placeholder="Qui va bénéficier de ces tâches ?"
                            getA11ySelectionMessage={{
                                onAdd: (item) => `${item} has been selected.`,
                            }}
                            onBlur={onBlur} // notify when input is touched
                            onChange={(_, option) => {
                                setValue("beneficiary", option?.value);
                                onChange(option?.value);
                                // add context benefciary here, je peux le recuperer depuis la modale du confirmation tasks
                                setBenefeciary(option?.value);
                                return;
                            }} // send value to hook form
                            checked={value}
                            // position="below"
                            unstable_pinned={false}
                            error={errors?.beneficiary ? true : false}
                            ref={ref}
                            checkable
                        />
                    )}
                    name="beneficiary"
                />
                {errors?.beneficiary && (
                    <ErrorMessage content={t(errors?.beneficiary.message)} />
                )}
            </Flex>
        </Flex>
    );
};

export const AccordionStyle = styled(Accordion)`
    .ui-accordion__title {
        padding-bottom: 0px;
    }
    .Accordion__checkbox--header {
        padding-bottom: 10px;
    }
    .ui-accordion__title__content-wrapper > .ui-box {
        background-repeat: no-repeat;
        /* background-position-y: 5px; */
    }
    .ui-accordion__title__content-wrapper {
        background-color: #e8ebfa;
    }
`;
